import {
  ArrowDownTrayIcon,
  DocumentTextIcon,
  PauseIcon,
  PhoneArrowDownLeftIcon,
  PhoneArrowUpRightIcon,
  PlayIcon,
} from '@heroicons/react/24/solid';
import * as Sentry from '@sentry/react';
import { useState } from 'react';
import WaveSurfer from 'wavesurfer.js';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ArtifactType } from '@/services/types/artifact-digest-types';

import { audioService } from '../../services/api/audioService';
import { ConversationSource } from '../../services/types/audio-conversation-types';
import { InspectArtifactButton } from '../artifact-inspector/InspectArtifactButton';
import AudioWaveform from './AudioWaveform';
import { AudioPlayerProps } from './types';
import { formatCallDuration } from './utils';

export const AudioConversationPlayerSkeleton = () => {
  return (
    <div className="p-4 bg-gray-25 rounded border border-gray-50 animate-pulse">
      <div className="flex items-start gap-3">
        <div className="h-3.5 w-3.5 bg-gray-100 rounded flex-shrink-0 mt-0.5" />
        <div className="flex-1">
          <div className="h-4 w-40 bg-gray-100 rounded mb-2" />
          <div className="h-3 w-28 bg-gray-100 rounded mb-1.5" />
          <div className="h-3 w-52 bg-gray-100 rounded" />
        </div>
        <div className="flex gap-1">
          <div className="h-6 w-6 bg-gray-100 rounded" />
          <div className="h-6 w-6 bg-gray-100 rounded" />
          <div className="h-6 w-6 bg-gray-100 rounded" />
        </div>
      </div>
    </div>
  );
};

const AudioConversationPlayer = ({
  conversation,
  onPlay,
  playingConversationId,
  enableInspector = false,
}: AudioPlayerProps) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [wavesurfer, setWavesurfer] = useState<WaveSurfer | null>(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);

  const isBlandAI = conversation.source === ConversationSource.BLAND_AI;
  const isCurrentlyPlaying =
    playingConversationId === conversation.id && isPlaying;

  // Lazy load audio only when user wants to play
  const fetchAudioUrl = async (): Promise<string | null> => {
    if (audioUrl) return audioUrl;
    if (!conversation.audio_path) return null;

    setIsLoadingAudio(true);
    try {
      const url = await audioService.fetchPresignedUrl(conversation.audio_path);
      setAudioUrl(url);
      return url;
    } catch (error) {
      Sentry.captureException(
        `Failed to fetch audio URL for ${conversation.id}`,
      );
      return null;
    } finally {
      setIsLoadingAudio(false);
    }
  };

  // Lazy load transcript PDF
  const fetchPdf = async (): Promise<string | null> => {
    if (pdfUrl) return pdfUrl;

    setIsLoadingPdf(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_APP_DJANGO_BACKEND_HOST}/api/transcript/${conversation.id}/pdf/`,
        { credentials: 'include' },
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
        return url;
      }
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setIsLoadingPdf(false);
    }
    return null;
  };

  // Handle play/pause
  const handlePlayPause = async (): Promise<void> => {
    if (!isExpanded) {
      setIsExpanded(true);
      const url = await fetchAudioUrl();
      if (url) {
        onPlay();
      }
      return;
    }

    if (wavesurfer) {
      if (!isPlaying) {
        onPlay();
      }
      wavesurfer.playPause();
    }
  };

  // Handle audio download
  const handleAudioDownload = async (): Promise<void> => {
    const url = await fetchAudioUrl();
    if (url) {
      const link = document.createElement('a');
      link.href = url;
      link.download = `audio-${conversation.id}.wav`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Handle waveform ready
  const handleWaveformReady = (ws: WaveSurfer): void => {
    setWavesurfer(ws);
    setTotalDuration(ws.getDuration());

    // Auto-play if this was just expanded
    if (!isPlaying) {
      ws.play();
    }

    ws.on('audioprocess', (time: number) => setCurrentTime(time));
    ws.on('play', () => setIsPlaying(true));
    ws.on('pause', () => setIsPlaying(false));
  };

  // Stop playing if another conversation starts
  if (playingConversationId !== conversation.id && wavesurfer && isPlaying) {
    wavesurfer.pause();
    setIsPlaying(false);
  }

  // Format date/time
  const dateObj = new Date(conversation.created_at || '');
  const formattedDate = dateObj.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
  });
  const formattedTime = dateObj.toLocaleTimeString('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });

  const handleTranscriptClick = async () => {
    const url = await fetchPdf();
    if (url) {
      window.open(url, '_blank', 'noreferrer');
    }
  };

  // Format duration in a human-readable way
  const formatDuration = (duration: string): string => {
    // Handle formats like "5:23", "1:02:15", "0:45", etc.
    const parts = duration.split(':');

    if (parts.length === 2) {
      // MM:SS format
      const minutes = parseInt(parts[0], 10);
      const seconds = parseInt(parts[1], 10);

      if (minutes === 0) {
        return `${seconds}s`;
      }
      if (seconds === 0) {
        return `${minutes}m`;
      }
      return `${minutes}m ${seconds}s`;
    }
    if (parts.length === 3) {
      // HH:MM:SS format
      const hours = parseInt(parts[0], 10);
      const minutes = parseInt(parts[1], 10);
      const seconds = parseInt(parts[2], 10);

      if (hours === 0) {
        if (minutes === 0) {
          return `${seconds}s`;
        }
        if (seconds === 0) {
          return `${minutes}m`;
        }
        return `${minutes}m ${seconds}s`;
      }
      if (minutes === 0 && seconds === 0) {
        return `${hours}h`;
      }
      if (seconds === 0) {
        return `${hours}h ${minutes}m`;
      }
      if (minutes === 0) {
        return `${hours}h ${seconds}s`;
      }
      return `${hours}h ${minutes}m`;
    }

    // Fallback to original duration if format is unexpected
    return duration;
  };

  const containerClasses =
    'p-4 bg-white border border-gray-100 rounded-lg shadow-sm';

  return (
    <div className={`${containerClasses} group`}>
      {/* Main content */}
      <div className="flex items-start gap-3">
        {/* Call direction icon */}
        <div className="flex-shrink-0 mt-1">
          {conversation.is_inbound ? (
            <PhoneArrowDownLeftIcon className="h-4 w-4 text-gray-400" />
          ) : (
            <PhoneArrowUpRightIcon className="h-4 w-4 text-gray-400" />
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Work log entry - primary content */}
          {conversation.artifact_digest?.work_log_entry ? (
            <div className="mb-3">
              <p className="text-sm leading-relaxed text-gray-900">
                {conversation.artifact_digest.work_log_entry}
              </p>
            </div>
          ) : (
            <div className="mb-3">
              <p className="text-sm text-gray-400 italic">
                No work log entry available
              </p>
            </div>
          )}

          {/* Metadata and actions row */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className="text-xs text-gray-500 font-medium">
                {formattedDate} at {formattedTime}
              </span>
              {conversation.duration && (
                <span className="text-xs text-gray-400 font-normal">
                  • {formatDuration(conversation.duration)}
                </span>
              )}
              {/* AI Badge */}
              {isBlandAI && (
                <span className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium bg-blue-50 text-blue-600">
                  AI
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              {/* Inspector - primary action for viewing transcript */}
              {enableInspector && conversation.transcript_artifact_id && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <InspectArtifactButton
                      artifactId={conversation.transcript_artifact_id}
                      artifactType={ArtifactType.AUDIO_CONVERSATION}
                      buttonClassName="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                      compact
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Inspect transcript</p>
                  </TooltipContent>
                </Tooltip>
              )}

              {/* Play/Pause */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handlePlayPause}
                    disabled={isLoadingAudio}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  >
                    {isLoadingAudio ? (
                      <div className="h-3.5 w-3.5 border-2 border-gray-200 border-t-transparent rounded-full animate-spin" />
                    ) : isCurrentlyPlaying ? (
                      <PauseIcon className="h-3.5 w-3.5" />
                    ) : (
                      <PlayIcon className="h-3.5 w-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{isCurrentlyPlaying ? 'Pause' : 'Play'} audio</p>
                </TooltipContent>
              </Tooltip>

              {/* Download PDF transcript */}
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    onClick={handleTranscriptClick}
                    disabled={isLoadingPdf}
                    className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                  >
                    {isLoadingPdf ? (
                      <div className="h-3.5 w-3.5 border-2 border-gray-200 border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <DocumentTextIcon className="h-3.5 w-3.5" />
                    )}
                  </button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Download transcript PDF</p>
                </TooltipContent>
              </Tooltip>

              {/* Download audio */}
              {conversation.audio_path && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <button
                      type="button"
                      onClick={handleAudioDownload}
                      disabled={isLoadingAudio}
                      className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors disabled:opacity-50"
                    >
                      {isLoadingAudio ? (
                        <div className="h-3.5 w-3.5 border-2 border-gray-200 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <ArrowDownTrayIcon className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Download audio file</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Expanded audio player */}
      {isExpanded && audioUrl && (
        <div className="mt-4 pt-4 border-t border-gray-75">
          <div className="flex items-center gap-3">
            <AudioWaveform
              audioUrl={audioUrl}
              onReady={handleWaveformReady}
              onPlay={() => setIsPlaying(true)}
              onPause={() => setIsPlaying(false)}
              isPlaying={isPlaying}
            />
            <div className="text-xs font-mono text-gray-300 whitespace-nowrap min-w-[50px] text-right">
              {formatCallDuration(
                Math.floor(currentTime),
                Math.floor(totalDuration),
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AudioConversationPlayer;
